// src/app/_service/space.service.ts
// Thin client for the server's Space endpoints (GET/POST /api/spaces, /api/spaces/<id>/members —
// see docs/SPACE_SERVICE.md in the server repo). Stage 1 added read-only support (getSpaces(),
// getPrivateSpaceId()); Stage 2 added createSpace() for the Space picker UI. Stage 3 (Space
// admin page) adds updateSpace(), member management (getMembers()/addMember()/
// updateMemberRole()/removeMember()), and getActiveUsers() — the last one is a wider "list
// every active user" endpoint than tasks/space_core_client.md's Stage 3 originally scoped
// (which called for a minimal username lookup only, for privacy); this is a deliberate,
// explicitly-requested widening for the Space admin member-role UI, not an oversight.

import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { tokenKey } from '../_util/data-target';
import { ApiResponse } from './data.service';
import { environment } from '../../environments/environment';

export type SpaceRole = 'OWNER' | 'ADMIN' | 'EDITOR' | 'VIEWER';

export interface Space {
  id: string;
  name: string;
  description?: string | null;
  type: 'PRIVATE' | 'SHARED';
  offline_policy: 'ALLOWED' | 'DISALLOWED';
  status: 'ACTIVE' | 'ARCHIVED';
  owner_user_id: string;
  owner_username: string;
  current_user_role: SpaceRole;
  created_at: string;
  created_by: string;
  modified_at: string;
}

export interface SpaceMember {
  space_id: string;
  user_id: string;
  username: string;
  role: SpaceRole;
  created_at: string;
  created_by: string;
}

export interface ActiveUser {
  id: string;
  username: string;
}

@Injectable({
  providedIn: 'root',
})
export class SpaceService {
  // Prefer env URL, but fall back to same-origin /api, same as DataRemoteService.
  private readonly baseUrl = environment.apiBaseUrl || '/api';

  constructor(private http: HttpClient) {}

  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem(tokenKey());
    const headers = new HttpHeaders();
    return token ? headers.set('Authorization', `Bearer ${token}`) : headers;
  }

  private handleError = (error: HttpErrorResponse): Observable<never> => {
    const message = error.error?.error?.message || error.message || 'Unknown error';
    return throwError(() => new Error(message));
  };

  /** GET /api/spaces — every Space the current user belongs to (their own PRIVATE one, plus any
   * SHARED ones they're a member of). */
  getSpaces(): Observable<Space[]> {
    const url = `${this.baseUrl}/spaces`;
    return this.http.get<ApiResponse<Space[]>>(url, { headers: this.getAuthHeaders() }).pipe(
      map((response) => {
        if (response.status === 'success' && response.data) return response.data;
        throw new Error(response.error?.message || 'Unknown error');
      }),
      catchError(this.handleError),
    );
  }

  /** Convenience: the id of the user's own PRIVATE Space (every user has exactly one), or null if
   * it can't be found (e.g. the list came back empty). */
  getPrivateSpaceId(): Observable<string | null> {
    return this.getSpaces().pipe(
      map((spaces) => spaces.find((space) => space.type === 'PRIVATE')?.id ?? null),
    );
  }

  /** POST /api/spaces — creates a new SHARED Space (a user's PRIVATE Space is provisioned
   * server-side and is not user-creatable). The caller becomes its OWNER. */
  createSpace(name: string, description?: string): Observable<Space> {
    const url = `${this.baseUrl}/spaces`;
    const payload: { name: string; type: 'SHARED'; description?: string } = { name, type: 'SHARED' };
    if (description) {
      payload.description = description;
    }
    return this.http.post<ApiResponse<Space>>(url, payload, { headers: this.getAuthHeaders() }).pipe(
      map((response) => {
        if (response.status === 'success' && response.data) return response.data;
        throw new Error(response.error?.message || 'Unknown error');
      }),
      catchError(this.handleError),
    );
  }

  /** PATCH /api/spaces/:id — rename a Space and/or change its description. Only the given
   * fields are changed server-side; omit a field to leave it unchanged. */
  updateSpace(spaceId: string, changes: { name?: string; description?: string }): Observable<Space> {
    const url = `${this.baseUrl}/spaces/${encodeURIComponent(spaceId)}`;
    return this.http.patch<ApiResponse<Space>>(url, changes, { headers: this.getAuthHeaders() }).pipe(
      map((response) => {
        if (response.status === 'success' && response.data) return response.data;
        throw new Error(response.error?.message || 'Unknown error');
      }),
      catchError(this.handleError),
    );
  }

  /** GET /api/spaces/:id/members — every member of the Space, with their role. */
  getMembers(spaceId: string): Observable<SpaceMember[]> {
    const url = `${this.baseUrl}/spaces/${encodeURIComponent(spaceId)}/members`;
    return this.http.get<ApiResponse<SpaceMember[]>>(url, { headers: this.getAuthHeaders() }).pipe(
      map((response) => {
        if (response.status === 'success' && response.data) return response.data;
        throw new Error(response.error?.message || 'Unknown error');
      }),
      catchError(this.handleError),
    );
  }

  /** POST /api/spaces/:id/members — grants a user a role in the Space (they must not
   * already be a member — use updateMemberRole() to change an existing member's role). */
  addMember(spaceId: string, userId: string, role: SpaceRole): Observable<SpaceMember> {
    const url = `${this.baseUrl}/spaces/${encodeURIComponent(spaceId)}/members`;
    return this.http
      .post<ApiResponse<SpaceMember>>(url, { user_id: userId, role }, { headers: this.getAuthHeaders() })
      .pipe(
        map((response) => {
          if (response.status === 'success' && response.data) return response.data;
          throw new Error(response.error?.message || 'Unknown error');
        }),
        catchError(this.handleError),
      );
  }

  /** PATCH /api/spaces/:id/members/:userId — changes an existing member's role. */
  updateMemberRole(spaceId: string, userId: string, role: SpaceRole): Observable<SpaceMember> {
    const url = `${this.baseUrl}/spaces/${encodeURIComponent(spaceId)}/members/${encodeURIComponent(userId)}`;
    return this.http.patch<ApiResponse<SpaceMember>>(url, { role }, { headers: this.getAuthHeaders() }).pipe(
      map((response) => {
        if (response.status === 'success' && response.data) return response.data;
        throw new Error(response.error?.message || 'Unknown error');
      }),
      catchError(this.handleError),
    );
  }

  /** DELETE /api/spaces/:id/members/:userId — revokes a member's access entirely. */
  removeMember(spaceId: string, userId: string): Observable<void> {
    const url = `${this.baseUrl}/spaces/${encodeURIComponent(spaceId)}/members/${encodeURIComponent(userId)}`;
    return this.http.delete<ApiResponse<void>>(url, { headers: this.getAuthHeaders() }).pipe(
      map((response) => {
        if (response.status === 'success') return;
        throw new Error(response.error?.message || 'Unknown error');
      }),
      catchError(this.handleError),
    );
  }

  /** GET /api/users — every active ("approved") NotesApp user, id/username only. Used by the
   * Space admin page to offer a role to any user, not just current members. */
  getActiveUsers(): Observable<ActiveUser[]> {
    const url = `${this.baseUrl}/users`;
    return this.http.get<ApiResponse<ActiveUser[]>>(url, { headers: this.getAuthHeaders() }).pipe(
      map((response) => {
        if (response.status === 'success' && response.data) return response.data;
        throw new Error(response.error?.message || 'Unknown error');
      }),
      catchError(this.handleError),
    );
  }
}
